import { useEffect, useState, type ReactNode } from 'react';
import { pim, PimEligibleRole, PimRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../utils';

const statusColor: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-300',
  active: 'bg-green-500/20 text-green-300',
  denied: 'bg-coral/20 text-coral',
  failed: 'bg-coral/20 text-coral',
  cancelled: 'bg-edge text-gray-400',
  expired: 'bg-edge text-gray-400',
  revoked: 'bg-edge text-gray-400',
};

function label(r: { role_name: string; client_id: string }): string {
  return r.client_id ? `${r.client_id}/${r.role_name}` : r.role_name;
}

function fmt(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

export default function PimPage() {
  const { me, hasRole } = useAuth();
  const canRequest = hasRole('pim-eligible');
  const isApprover = hasRole('pim-approver') || (me?.capability === 'operator' && hasRole('iga-admin'));
  const canManage = me?.capability === 'tenant-admin' || hasRole('iga-admin');

  const [eligible, setEligible] = useState<PimEligibleRole[]>([]);
  const [mine, setMine] = useState<PimRequest[]>([]);
  const [all, setAll] = useState<PimRequest[]>([]);
  const [error, setError] = useState('');

  // Request form
  const [roleKey, setRoleKey] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [justification, setJustification] = useState('');

  // Eligible-role form (admins)
  const [newRole, setNewRole] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newMax, setNewMax] = useState(240);

  const load = async () => {
    setError('');
    try {
      const [e, m] = await Promise.all([pim.listEligible(), pim.listRequests({ mine: true })]);
      setEligible(e.data);
      setMine(m.data);
      if (isApprover) setAll((await pim.listRequests()).data);
    } catch (err) {
      setError(errMsg(err));
    }
  };

  useEffect(() => {
    void load();
    // Load once on mount; roles (isApprover) are fixed for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const selected = eligible.find((e) => `${e.client_id}|${e.role_name}` === roleKey);

  const submit = () =>
    run(async () => {
      if (!selected) return;
      await pim.request({
        roleName: selected.role_name,
        clientId: selected.client_id || undefined,
        durationMinutes: minutes,
        justification,
      });
      setJustification('');
    });

  const pendingForMe = all.filter((r) => r.status === 'pending' && r.kc_user_id !== me?.sub);
  const activeAll = all.filter((r) => r.status === 'active');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Privileged Access (PIM)</h2>
      <p className="text-sm text-gray-400">
        Just-in-Time-Rechte: zeitlich begrenzt, begründet, von einer zweiten Person genehmigt und
        nach Ablauf automatisch entzogen.
      </p>
      {error && <p className="text-coral">{error}</p>}

      {canRequest && (
        <section className="card space-y-3">
          <h3 className="font-medium text-coral">Rolle anfordern</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Rolle</label>
              <select
                className="input"
                value={roleKey}
                onChange={(e) => {
                  setRoleKey(e.target.value);
                  const r = eligible.find((x) => `${x.client_id}|${x.role_name}` === e.target.value);
                  if (r) setMinutes((m) => Math.min(m, r.max_minutes));
                }}
              >
                <option value="">— wählen —</option>
                {eligible.map((e) => (
                  <option key={e.id} value={`${e.client_id}|${e.role_name}`}>
                    {label(e)} (max. {e.max_minutes} min)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Dauer (Minuten)</label>
              <input
                className="input"
                type="number"
                min={1}
                max={selected?.max_minutes}
                value={minutes}
                onChange={(e) => setMinutes(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div>
              <label className="label">Begründung (Ticket, Anlass)</label>
              <input
                className="input"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
              />
            </div>
          </div>
          <button
            className="btn-primary"
            disabled={!selected || minutes < 1 || justification.trim().length < 5}
            onClick={submit}
          >
            Antrag stellen
          </button>
        </section>
      )}

      <section className="card overflow-x-auto">
        <h3 className="mb-3 font-medium text-coral">Meine Anträge &amp; aktiven Rechte</h3>
        <RequestTable
          rows={mine}
          actions={(r) =>
            r.status === 'pending' ? (
              <button className="btn-ghost" onClick={() => run(() => pim.cancel(r.id))}>
                Zurückziehen
              </button>
            ) : r.status === 'active' ? (
              <button className="btn-ghost" onClick={() => run(() => pim.revoke(r.id))}>
                Vorzeitig beenden
              </button>
            ) : null
          }
        />
      </section>

      {isApprover && (
        <>
          <section className="card overflow-x-auto">
            <h3 className="mb-3 font-medium text-coral">Offene Genehmigungen</h3>
            <RequestTable
              rows={pendingForMe}
              actions={(r) => (
                <div className="flex gap-2">
                  <button className="btn-primary" onClick={() => run(() => pim.approve(r.id))}>
                    Genehmigen
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() =>
                      run(() => pim.deny(r.id, window.prompt('Ablehnungsgrund?') ?? ''))
                    }
                  >
                    Ablehnen
                  </button>
                </div>
              )}
            />
          </section>
          <section className="card overflow-x-auto">
            <h3 className="mb-3 font-medium text-coral">Aktive privilegierte Rechte</h3>
            <RequestTable
              rows={activeAll}
              actions={(r) => (
                <button className="btn-ghost" onClick={() => run(() => pim.revoke(r.id))}>
                  Entziehen
                </button>
              )}
            />
          </section>
        </>
      )}

      {canManage && (
        <section className="card space-y-3">
          <h3 className="font-medium text-coral">Berechtigte Rollen (eligible)</h3>
          <div className="grid grid-cols-4 gap-3">
            <input
              className="input"
              placeholder="Rolle"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
            />
            <input
              className="input"
              placeholder="Client-ID (leer = Realm-Rolle)"
              value={newClient}
              onChange={(e) => setNewClient(e.target.value)}
            />
            <input
              className="input"
              type="number"
              min={1}
              value={newMax}
              onChange={(e) => setNewMax(parseInt(e.target.value, 10) || 0)}
            />
            <button
              className="btn-primary"
              disabled={!newRole || newMax < 1}
              onClick={() =>
                run(async () => {
                  await pim.upsertEligible({
                    roleName: newRole,
                    clientId: newClient || undefined,
                    maxMinutes: newMax,
                  });
                  setNewRole('');
                  setNewClient('');
                })
              }
            >
              Hinzufügen
            </button>
          </div>
          <ul className="space-y-1 text-sm">
            {eligible.map((e) => (
              <li key={e.id} className="flex items-center gap-3">
                <span className="text-white">{label(e)}</span>
                <span className="text-gray-500">max. {e.max_minutes} min</span>
                {!e.enabled && <span className="badge bg-edge text-gray-400">deaktiviert</span>}
                <button className="btn-ghost px-2 py-0.5 text-xs" onClick={() => run(() => pim.deleteEligible(e.id))}>
                  Entfernen
                </button>
              </li>
            ))}
            {eligible.length === 0 && <li className="text-gray-500">Keine berechtigten Rollen.</li>}
          </ul>
        </section>
      )}
    </div>
  );
}

function RequestTable({
  rows,
  actions,
}: {
  rows: PimRequest[];
  actions: (r: PimRequest) => ReactNode;
}) {
  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-edge">
          <th className="th">Benutzer</th>
          <th className="th">Rolle</th>
          <th className="th">Dauer</th>
          <th className="th">Begründung</th>
          <th className="th">Status</th>
          <th className="th">Läuft ab</th>
          <th className="th"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-edge/50">
            <td className="td">{r.username}</td>
            <td className="td">{label(r)}</td>
            <td className="td">{r.duration_minutes} min</td>
            <td className="td">{r.justification}</td>
            <td className="td">
              <span className={`badge ${statusColor[r.status] ?? ''}`}>{r.status}</span>
            </td>
            <td className="td">{fmt(r.expires_at)}</td>
            <td className="td">{actions(r)}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td className="td text-gray-500" colSpan={7}>
              Keine Einträge.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
